// src/services/helpdeskAIInsightsService.js
// ENHANCED: Production-ready AI insights with advanced analytics, caching, and detailed recommendations
//
// --- UPDATE (AI Insights v3) connected to the FlowSense AI/ML system:
//   * server insights from /api/v1/ai-insights/helpdesk: live SQL on tickets + the Phone AI channel
//     (public reports, hot-spot clusters, calls, classifier accuracy), ML-engine volume forecast,
//     Ollama briefing, feedback learning
//   * getMergedInsights(): server first, local heuristics fill the gaps (dedupe by key); actions are
//     normalised to the HelpdeskPage action router (auto_assign with ticket ids, navigate, retrain)
//   * every insight has a stable id, so ratings train the shared engine (rateInsight)
//   * local analyzers fixed: reopen_count, first_response_at, resolved_at, sla_due_at,
//     severity order incl. success/low, cache keyed on ticket content, single-flight requests
// --- END UPDATE

/**
 * Helpdesk AI Insights Service - Production Version
 * Provides intelligent analysis and actionable recommendations for support ticket management
 * Features: Caching, performance tracking, detailed metrics, export capabilities
 */

import api from './api'; // --- UPDATE: authenticated FlowSense client (same as the rest of the Helpdesk)
import webSocketService from './websocketService'; // --- UPDATE: live refresh

// --- UPDATE
const ACTIVE_STATUSES = ['new', 'open', 'in_progress', 'pending_customer', 'pending_internal'];
const RESOLVED_STATUSES = ['resolved', 'closed'];
const isActive = (t) => ACTIVE_STATUSES.includes(t.status);
const hoursSince = (d) => (d ? (Date.now() - new Date(d).getTime()) / 3600000 : 0);
const unwrapBody = (r) => (r && r.data ? r.data : r);
// --- END UPDATE

class HelpdeskAIInsightsService {
  constructor() {
    this.debugMode = process.env.NODE_ENV === 'development';
    this.cache = new Map();
    this.cacheTTL = 300000; // 5 minutes
    this.performanceMetrics = [];
    this.insightHistory = [];
    this.version = '3.0.0';
    // --- UPDATE
    this.serverTTL = 30000;
    this.inflight = new Map();
    this.lastMeta = null;
    // --- END UPDATE
  }

  /**
   * Enable or disable debug logging
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    this.log('info', 'Debug mode changed', { enabled });
  }

  /**
   * Enhanced debug logger with levels
   */
  log(level, message, data = null) {
    if (!this.debugMode && (level === 'debug' || level === 'info')) return;

    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      service: 'HelpdeskAIInsightsService',
      message,
      data,
    };

    const styles = {
      debug: 'color: #888; font-size: 11px',
      info: 'color: #2196F3; font-weight: bold',
      warn: 'color: #FF9800; font-weight: bold',
      error: 'color: #F44336; font-weight: bold',
      success: 'color: #4CAF50; font-weight: bold',
    };

    const method = level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
    method(`%c[AI Insights] ${message}`, styles[level] || styles.info, data || '');

    return logEntry;
  }

  /**
   * Get cached data if available and not expired
   */
  getCached(key, ttl = this.cacheTTL) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > ttl) {
      this.cache.delete(key);
      this.log('debug', 'Cache expired', { key });
      return null;
    }

    this.log('debug', 'Cache hit', { key });
    return cached.data;
  }

  /**
   * Set cached data with metadata
   */
  setCached(key, data) {
    if (this.cache.size >= 100) this.cache.delete(this.cache.keys().next().value); // --- UPDATE: bounded
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      version: this.version,
    });
    this.log('debug', 'Cache set', { key, size: this.cache.size });
  }

  /**
   * Clear all cache
   */
  clearCache() {
    const size = this.cache.size;
    this.cache.clear();
    this.log('info', 'Cache cleared', { previousSize: size });
    return { cleared: size };
  }

  /**
   * Track performance metrics
   */
  trackPerformance(operation, duration, metadata = {}) {
    const metric = {
      operation,
      duration,
      timestamp: new Date().toISOString(),
      ...metadata,
    };

    this.performanceMetrics.push(metric);

    // Keep only last 100 metrics
    if (this.performanceMetrics.length > 100) {
      this.performanceMetrics.shift();
    }

    this.log('debug', `Performance: ${operation}`, { duration: `${duration.toFixed(2)}ms` });
    return metric;
  }

  // ────────────────────────────────────────────────────────────────────────────
  // --- UPDATE: SERVER (FlowSense AI Insights engine)
  // ────────────────────────────────────────────────────────────────────────────

  _once(key, fn) {
    if (this.inflight.has(key)) return this.inflight.get(key);
    const p = fn().finally(() => this.inflight.delete(key));
    this.inflight.set(key, p);
    return p;
  }

  /**
   * Server-side helpdesk insights.
   * @returns {Promise<{success, data, meta, stats, narrative}>}
   */
  async getServerInsights({ days = 30, narrative = true, refresh = false, signal } = {}) {
    const key = `server-hd-${days}-${narrative}`;
    if (!refresh) {
      const hit = this.getCached(key, this.serverTTL);
      if (hit) return hit;
    }
    return this._once(key, async () => {
      const t0 = performance.now();
      try {
        const body = unwrapBody(await api.get('/ai-insights/helpdesk', { params: { days, narrative: narrative ? 1 : 0, refresh: refresh ? 1 : 0 }, signal }));
        const out = { success: true, data: body.data || [], meta: body.meta || null, stats: body.stats || null, narrative: body.narrative || null };
        this.lastMeta = { ...out.meta, stats: out.stats, narrative: out.narrative };
        this.setCached(key, out);
        this.trackPerformance('getServerInsights', performance.now() - t0, { insightCount: out.data.length, serverMs: out.meta?.durationMs });
        return out;
      } catch (error) {
        this.log('warn', 'AI insights engine unavailable — using local analysis', { error: error.message, status: error.response?.status });
        return { success: false, data: [], error: error.message };
      }
    });
  }

  /** Map server action types onto the HelpdeskPage action router */
  _normaliseAction(insight, tickets) {
    const i = { ...insight };
    if (i.actionType === 'bulk-assign') {
      const ids = tickets.filter((t) => isActive(t) && !t.assigned_agent_id).map((t) => t.id);
      i.actionType = 'auto_assign';
      i.actionPayload = { ...(i.actionPayload || {}), ticketIds: ids };
    }
    return i;
  }

  /**
   * Server + local insights in one ranked list. This is what the Helpdesk AI panel should call.
   * @returns {Promise<Array>}
   */
  async getMergedInsights(tickets = [], stats = null, opts = {}) {
    const local = this.generateInsights(tickets, stats);
    const server = await this.getServerInsights(opts);
    if (!server.success || !server.data.length) return local;
    const serverKeys = new Set(server.data.map((i) => i.key));
    const list = [
      ...server.data.map((i) => this._normaliseAction(i, tickets)),
      ...local.filter((i) => !serverKeys.has(i.key)),
    ];
    if (server.narrative) {
      list.unshift({
        id: 'helpdesk:hd.briefing', key: 'hd.briefing', type: 'info', severity: 'low', icon: 'MdAutoAwesome',
        title: 'AI helpdesk briefing', message: server.narrative, action: null, source: 'server', engine: 'llm', metrics: {}, recommendations: [],
      });
    }
    return list;
  }

  /**
   * Send helpful (1) / not helpful (-1). Five "not helpful" votes mute a non-critical insight type for everyone.
   */
  async rateInsight(insightOrId, rating, comment = null) {
    const insight = typeof insightOrId === 'object' && insightOrId ? insightOrId : null;
    const id = insight ? insight.id : insightOrId;
    const body = unwrapBody(await api.post(`/ai-insights/${encodeURIComponent(id)}/rate`, {
      rating, comment, insight: insight ? { title: insight.title, metrics: insight.metrics, severity: insight.severity } : null,
    }));
    this.clearCache();
    return body.data || body;
  }

  async getStatus() {
    try { return unwrapBody(await api.get('/ai-insights/status')).data; } catch (_) { return null; }
  }

  async getHistory(days = 30) {
    try { return unwrapBody(await api.get('/ai-insights/history/helpdesk', { params: { days } })).data || []; } catch (_) { return []; }
  }

  getLastMeta() {
    return this.lastMeta;
  }

  /** Live refresh: onChange (debounced) whenever a ticket or public report changes. Returns unsubscribe. */
  watch(onChange, { debounceMs = 3000 } = {}) {
    let t = null;
    const fire = () => {
      clearTimeout(t);
      t = setTimeout(() => { this.clearCache(); onChange && onChange(); }, debounceMs);
    };
    const unsubs = [];
    try {
      webSocketService.connect();
      const u = webSocketService.subscribe('helpdesk', fire);
      unsubs.push(typeof u === 'function' ? u : () => webSocketService.unsubscribe?.('helpdesk', fire));
    } catch (e) {
      this.log('warn', 'Live insight refresh unavailable', e.message);
    }
    return () => { clearTimeout(t); unsubs.forEach((u) => { try { u(); } catch (_) { /* ignore */ } }); };
  }
  // --- END UPDATE

  /**
   * Main method: Analyze tickets and generate insights
   * @param {Array} tickets - Array of ticket objects
   * @param {Object} stats - Ticket statistics
   * @returns {Array} - Array of insight objects
   */
  generateInsights(tickets, stats) {
    const startTime = performance.now();

    // Enhanced safety checks
    if (!tickets || !Array.isArray(tickets)) {
      this.log('warn', 'Invalid tickets array provided', { tickets });
      return [];
    }

    // --- UPDATE: always fill missing fields from the tickets themselves
    stats = { ...this._calculateMinimalStats(tickets), ...((stats && typeof stats === 'object') ? stats : {}) };
    // --- END UPDATE

    this.log('info', 'Generating insights', {
      ticketCount: tickets.length,
      stats,
      version: this.version,
    });

    try {
      const insights = [];

      // Check cache first (--- UPDATE: keyed on ticket content, not only counts)
      const cacheKey = `insights-v${this.version}-${this._fingerprint(tickets)}-${JSON.stringify(stats)}`;
      const cached = this.getCached(cacheKey);
      if (cached) {
        this.log('info', 'Returning cached insights', { count: cached.length });
        return cached;
      }

      // Run all analysis methods
      const analyses = [
        { method: this.analyzeSLABreaches, name: 'SLA Breaches', key: 'hd.sla' },
        { method: this.analyzeResponseTime, name: 'Response Time', key: 'hd.first_response' },
        { method: this.analyzePriorityDistribution, name: 'Priority Distribution', key: 'hd.priority' },
        { method: this.analyzeUnassignedTickets, name: 'Unassigned Tickets', key: 'hd.unassigned' },
        { method: this.analyzeCategoryPatterns, name: 'Category Patterns', key: null },
        { method: this.analyzeResolutionRate, name: 'Resolution Rate', key: null },
        { method: this.analyzeAgentWorkload, name: 'Agent Workload', key: 'hd.workload' },
        { method: this.analyzePendingTickets, name: 'Pending Tickets', key: 'hd.pending' },
        { method: this.analyzeTicketTrends, name: 'Ticket Trends', key: null },
        { method: this.analyzeCustomerSatisfaction, name: 'Customer Satisfaction', key: 'hd.satisfaction' },
        { method: this.analyzeEscalations, name: 'Escalations', key: 'hd.escalated' }, // --- UPDATE
      ];

      analyses.forEach(({ method, name, key }) => {
        try {
          const insight = method.call(this, tickets, stats);
          if (insight) {
            // --- UPDATE: stable id/key (matches the server engine so ratings line up)
            insight.key = insight.key || key || `hd.${name.toLowerCase().replace(/\s+/g, '_')}`;
            insight.id = insight.id || `helpdesk:${insight.key}`;
            insight.source = 'local';
            // --- END UPDATE
            insights.push(this._normaliseAction(insight, tickets));
            this.log('debug', `${name} insight generated`, insight);
          }
        } catch (error) {
          this.log('error', `Error in ${name} analysis`, {
            error: error.message,
            stack: error.stack,
          });
        }
      });

      // Sort by severity (--- UPDATE: success/info rank last instead of 999 ties)
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4, success: 5 };
      insights.sort((a, b) =>
        (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9) || (a.priority || 99) - (b.priority || 99)
      );

      // Cache results
      this.setCached(cacheKey, insights);

      // Track performance
      const duration = performance.now() - startTime;
      this.trackPerformance('generateInsights', duration, {
        insightCount: insights.length,
        ticketCount: tickets.length,
      });

      // Store in history
      this.insightHistory.push({
        timestamp: new Date().toISOString(),
        insightCount: insights.length,
        ticketCount: tickets.length,
      });
      if (this.insightHistory.length > 50) this.insightHistory.shift();

      this.log('info', 'Insights generated successfully', {
        count: insights.length,
        duration: `${duration.toFixed(2)}ms`,
      });

      return insights;
    } catch (error) {
      this.log('error', 'Error generating insights', {
        error: error.message,
        stack: error.stack,
      });
      return [];
    }
  }

  /** --- UPDATE: cheap content hash so edits invalidate the local cache */
  _fingerprint(tickets) {
    let h = 0;
    for (const t of tickets) {
      const s = `${t.id}|${t.status}|${t.updated_at || ''}|${t.assigned_agent_id || ''}`;
      for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    }
    return `${tickets.length}:${h}`;
  }

  /**
   * Calculate minimal stats from tickets if backend doesn't provide them
   */
  _calculateMinimalStats(tickets) {
    const now = Date.now();
    return {
      total: tickets.length,
      active_tickets: tickets.filter(isActive).length,
      resolved_tickets: tickets.filter((t) => RESOLVED_STATUSES.includes(t.status)).length,
      // --- UPDATE: breached from sla_status or from the SLA deadline itself
      breached: tickets.filter((t) => t.sla_status === 'breached' || (isActive(t) && t.sla_due_at && new Date(t.sla_due_at).getTime() < now)).length,
    };
  }

  /**
   * --- UPDATE: Connect to new SLA terminology (at_risk, compliant, breached) ---
   * Analyze SLA breaches with enhanced recommendations
   */
  analyzeSLABreaches(tickets, stats) {
    const now = Date.now();
    const breachedCount = stats?.breached || tickets.filter((t) => t.sla_status === 'breached').length;
    // Map both old and new terminology for safety (--- UPDATE: plus "due within 4 h" from sla_due_at)
    const atRiskSLA = tickets.filter((t) => ['at_risk', 'critical', 'warning'].includes(t.sla_status)
      || (isActive(t) && t.sla_due_at && new Date(t.sla_due_at).getTime() > now && new Date(t.sla_due_at).getTime() - now < 4 * 3600000)).length;
    const threshold = 5;

    if (breachedCount > threshold || atRiskSLA > 3) {
      const severity = breachedCount > 15 ? 'critical' : breachedCount > 10 ? 'high' : 'medium';
      const percentage = stats.total > 0 ? ((breachedCount / stats.total) * 100).toFixed(1) : 0;

      return {
        type: 'warning',
        severity,
        icon: 'MdErrorOutline',
        title: 'SLA Breach Alert',
        message: `${breachedCount} tickets have breached SLA (${percentage}%), and ${atRiskSLA} are currently AT RISK. Immediate action required to prevent further escalation.`,
        action: 'View At-Risk Tickets',
        actionType: 'filter',
        actionPayload: { filter: 'sla_status', value: 'at_risk' },
        metrics: {
          breached: breachedCount,
          atRisk: atRiskSLA,
          percentage,
          impactScore: (breachedCount * 3 + atRiskSLA * 2),
        },
        recommendations: [
          'Immediately prioritize SLA-breached tickets for resolution',
          'Convert highly technical Helpdesk tickets into Field Ops Work Orders to engage specialized crews',
          'Implement automated escalation for tickets approaching SLA deadline',
          'Consider adding support staff during peak hours',
          'Analyze common causes of SLA breaches for process improvement',
        ],
        priority: 1,
      };
    }

    return null;
  }

  /**
   * Analyze response time patterns
   * --- UPDATE: "waiting" = active tickets with no first_response_at (falls back to status 'new')
   */
  analyzeResponseTime(tickets) {
    const hasField = tickets.some((t) => Object.prototype.hasOwnProperty.call(t, 'first_response_at'));
    const waiting = tickets.filter((t) => (hasField ? isActive(t) && !t.first_response_at : t.status === 'new') && t.created_at);

    if (waiting.length === 0) return null;

    const ages = waiting.map((ticket) => hoursSince(ticket.created_at));

    const avgAge = ages.reduce((sum, age) => sum + age, 0) / ages.length;
    const oldestAge = Math.max(...ages);

    if (avgAge > 4) {
      return {
        type: 'warning',
        severity: avgAge > 12 ? 'critical' : avgAge > 8 ? 'high' : 'medium',
        icon: 'MdAccessTime',
        title: 'Slow Initial Response',
        message: `${waiting.length} tickets have had no first response, waiting ${avgAge.toFixed(1)} hours on average (oldest: ${oldestAge.toFixed(1)}h). Customers are waiting too long for first contact.`,
        action: 'View New Tickets',
        actionType: 'filter',
        actionPayload: { filter: 'status', value: 'new' },
        metrics: {
          averageAge: avgAge.toFixed(1),
          oldestAge: oldestAge.toFixed(1),
          newTickets: waiting.length,
          targetResponseTime: '4h',
        },
        recommendations: [
          'Assign tickets immediately upon creation using auto-assignment',
          'Use the AI reply draft to send the first response faster',
          'Review staff availability and scheduling during ticket creation times',
          'Set up alerts for tickets waiting longer than target response time',
        ],
        priority: 2,
      };
    }

    return null;
  }

  /**
   * Enhanced priority distribution analysis
   */
  analyzePriorityDistribution(tickets) {
    const priorityCounts = {
      critical: 0,
      urgent: 0,
      high: 0,
      medium: 0,
      low: 0,
    };

    const active = tickets.filter(isActive); // --- UPDATE: only open work matters for load
    active.forEach((ticket) => {
      const priority = ticket.priority || 'medium';
      priorityCounts[priority] = (priorityCounts[priority] || 0) + 1;
    });

    const highPriorityCount = priorityCounts.critical + priorityCounts.urgent + priorityCounts.high;
    const totalTickets = active.length;
    const highPriorityPercent = totalTickets > 0 ? (highPriorityCount / totalTickets) * 100 : 0;

    if (highPriorityCount > totalTickets * 0.4 && totalTickets > 20) {
      return {
        type: 'warning',
        severity: 'high',
        icon: 'MdPriorityHigh',
        title: 'High Priority Overload',
        message: `${highPriorityCount} open tickets (${highPriorityPercent.toFixed(1)}%) marked as high priority. This may indicate priority inflation or genuine crisis.`,
        action: 'Review High Priority',
        actionType: 'filter',
        actionPayload: { filter: 'priority', value: 'high' },
        metrics: {
          critical: priorityCounts.critical,
          urgent: priorityCounts.urgent,
          high: priorityCounts.high,
          medium: priorityCounts.medium,
          low: priorityCounts.low,
          percentage: highPriorityPercent.toFixed(1),
          expectedPercentage: '20-30%',
        },
        recommendations: [
          'Review and adjust priority assignment guidelines with clear criteria',
          'Convert critical technical issues directly to FlowSense Work Orders to dispatch field engineers',
          'Let the AI classifier suggest priority from the ticket content',
          'Train support team on proper priority classification',
        ],
        priority: 3,
      };
    }

    return null;
  }

  /**
   * Analyze unassigned tickets with urgency
   */
  analyzeUnassignedTickets(tickets) {
    const unassigned = tickets.filter((t) => isActive(t) && !t.assigned_agent_id); // --- UPDATE: active only
    const threshold = 10;

    if (unassigned.length > threshold || unassigned.some((t) => ['critical', 'urgent'].includes(t.priority))) {
      const urgentUnassigned = unassigned.filter((t) =>
        t.priority === 'critical' || t.priority === 'urgent'
      ).length;

      const newUnassigned = unassigned.filter((t) => t.status === 'new').length;

      return {
        type: 'warning',
        severity: urgentUnassigned > 5 ? 'critical' : 'high',
        icon: 'MdAssignment',
        title: 'Unassigned Tickets Backlog',
        message: `${unassigned.length} tickets unassigned (${urgentUnassigned} urgent/critical, ${newUnassigned} new). Risk of customer dissatisfaction and SLA breach.`,
        action: 'Auto-Assign Tickets',
        actionType: 'bulk-assign',
        actionPayload: { assignUnassigned: true },
        metrics: {
          unassigned: unassigned.length,
          urgent: urgentUnassigned,
          new: newUnassigned,
          percentage: ((unassigned.length / Math.max(1, tickets.length)) * 100).toFixed(1),
          avgUnassignedTime: this._calculateAvgAge(unassigned).toFixed(1),
        },
        recommendations: [
          'Enable round-robin auto-assignment for new tickets immediately',
          'Review agent availability, capacity, and current workload',
          'Implement skill-based routing for specialized ticket types',
          'Set up critical alert for unassigned urgent tickets',
        ],
        priority: 1,
      };
    }

    return null;
  }

  /**
   * Helper: Calculate average age of tickets in hours
   */
  _calculateAvgAge(tickets) {
    if (tickets.length === 0) return 0;
    const ages = tickets.map((t) => hoursSince(t.created_at));
    return ages.reduce((sum, age) => sum + age, 0) / ages.length;
  }

  /**
   * Analyze category patterns for resource planning
   */
  analyzeCategoryPatterns(tickets) {
    const categoryCounts = {};
    tickets.forEach((ticket) => {
      const category = ticket.category || 'general_inquiry';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    const sortedCategories = Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1]);

    if (tickets.length >= 20 && sortedCategories.length > 0 && sortedCategories[0][1] > tickets.length * 0.35) {
      const dominantCategory = sortedCategories[0][0];
      const count = sortedCategories[0][1];
      const percentage = ((count / tickets.length) * 100).toFixed(1);

      return {
        key: `hd.category.${dominantCategory}`, // --- UPDATE: matches server key
        type: 'info',
        severity: 'medium',
        icon: 'MdBarChart',
        title: `Dominant Category: ${dominantCategory.replace(/_/g, ' ').toUpperCase()}`,
        message: `${percentage}% (${count}) of tickets are ${dominantCategory.replace(/_/g, ' ')} related. High concentration suggests need for specialized resources or self-service content.`,
        action: 'View Category',
        actionType: 'filter',
        actionPayload: { filter: 'category', value: dominantCategory },
        metrics: {
          category: dominantCategory,
          count,
          percentage,
          totalCategories: sortedCategories.length,
          secondCategory: sortedCategories[1] ? sortedCategories[1][0] : null,
          secondCount: sortedCategories[1] ? sortedCategories[1][1] : 0,
        },
        recommendations: [
          `Create comprehensive FAQ and knowledge base for ${dominantCategory.replace(/_/g, ' ')}`,
          'Consider forming specialized team or expert for this category',
          'Analyze root causes to prevent recurring issues',
          'Add proactive public notices to reduce incoming tickets',
        ],
        priority: 4,
      };
    }

    return null;
  }

  /**
   * Analyze resolution rate and efficiency
   */
  analyzeResolutionRate(tickets, stats) {
    if (!stats.total) return null;

    const resolutionRate = ((stats.resolved_tickets / stats.total) * 100).toFixed(1);
    const rate = parseFloat(resolutionRate);
    const avgResolutionTime = this._calculateAvgResolutionTime(tickets);

    if (rate > 80 && stats.total >= 10) {
      return {
        key: 'hd.resolution_high',
        type: 'success',
        severity: 'low',
        icon: 'MdCheckCircle',
        title: 'Excellent Resolution Performance',
        message: `${resolutionRate}% resolution rate with avg resolution time of ${avgResolutionTime}h. Outstanding team performance!`,
        action: null,
        metrics: {
          resolutionRate: rate,
          resolved: stats.resolved_tickets,
          total: stats.total,
          avgResolutionTime,
          targetRate: '75%',
        },
        recommendations: [
          'Document and share successful resolution strategies',
          'Recognize and reward high-performing agents',
          'Maintain this performance with regular process reviews',
        ],
        priority: 10,
      };
    } else if (rate < 50 && stats.total > 30) {
      return {
        key: 'hd.resolution_low',
        type: 'warning',
        severity: 'high',
        icon: 'MdTrendingUp',
        title: 'Low Resolution Rate',
        message: `Only ${resolutionRate}% tickets resolved with ${avgResolutionTime}h avg time. Review workflows, identify blockers, and improve processes.`,
        action: 'Analyze Stalled Tickets',
        actionType: 'analyze',
        actionPayload: { metric: 'resolution-rate' },
        metrics: {
          resolutionRate: rate,
          resolved: stats.resolved_tickets,
          pending: stats.active_tickets,
          avgResolutionTime,
          targetRate: '75%',
          gap: (75 - rate).toFixed(1),
        },
        recommendations: [
          'Identify and remove common blockers preventing resolution',
          'Look at tickets waiting on field work (linked Work Orders)',
          'Implement clear escalation procedures for complex tickets',
          'Analyze tickets stuck in pending status',
        ],
        priority: 2,
      };
    }

    return null;
  }

  /**
   * Helper: Calculate average resolution time
   * --- UPDATE: resolution_time_minutes / resolved_at first, updated_at only as last resort
   */
  _calculateAvgResolutionTime(tickets) {
    const times = tickets
      .filter((t) => RESOLVED_STATUSES.includes(t.status))
      .map((t) => {
        if (Number(t.resolution_time_minutes) > 0) return Number(t.resolution_time_minutes) / 60;
        const end = t.resolved_at || t.closed_at || t.updated_at;
        return t.created_at && end ? (new Date(end) - new Date(t.created_at)) / 3600000 : null;
      })
      .filter((h) => h !== null && h >= 0);

    if (times.length === 0) return 0;

    const avg = times.reduce((sum, time) => sum + time, 0) / times.length;
    return avg.toFixed(1);
  }

  /**
   * Analyze agent workload distribution
   */
  analyzeAgentWorkload(tickets) {
    const agentLoads = {};

    tickets.forEach((ticket) => {
      if (ticket.assigned_agent_id) {
        const agentName = ticket.agent_name || ticket.assigned_agent_name || `Agent ${ticket.assigned_agent_id}`;
        if (!agentLoads[agentName]) {
          agentLoads[agentName] = { total: 0, active: 0, name: agentName, id: ticket.assigned_agent_id };
        }
        agentLoads[agentName].total++;
        if (isActive(ticket)) {
          agentLoads[agentName].active++;
        }
      }
    });

    const loads = Object.values(agentLoads);
    if (loads.length < 2) return null;

    const activeLoads = loads.map((l) => l.active);
    const avgLoad = activeLoads.reduce((a, b) => a + b, 0) / activeLoads.length;
    const maxLoad = Math.max(...activeLoads);
    const minLoad = Math.min(...activeLoads);
    const imbalance = maxLoad - minLoad;
    const maxAgent = loads.find((l) => l.active === maxLoad);
    const minAgent = loads.find((l) => l.active === minLoad);

    if (imbalance > 10 && avgLoad > 5) {
      return {
        type: 'warning',
        severity: 'medium',
        icon: 'MdGroup',
        title: 'Workload Imbalance Detected',
        message: `Significant workload imbalance: ${maxAgent.name} has ${maxLoad} active tickets vs ${minAgent.name} with ${minLoad}. Uneven distribution affects team efficiency.`,
        action: 'Redistribute Load',
        actionType: 'redistribute',
        actionPayload: { balance: true, from: maxAgent.id, to: minAgent.id },
        metrics: {
          maxLoad,
          minLoad,
          avgLoad: avgLoad.toFixed(1),
          imbalance,
          maxAgent: maxAgent.name,
          minAgent: minAgent.name,
          agentCount: loads.length,
        },
        recommendations: [
          'Reassign tickets from overloaded to underloaded agents',
          'Review agent availability, shifts, and working hours',
          'Set maximum concurrent ticket limits per agent',
        ],
        priority: 4,
      };
    }

    return null;
  }

  /**
   * Analyze pending tickets requiring action
   */
  analyzePendingTickets(tickets) {
    const pendingCustomer = tickets.filter((t) => t.status === 'pending_customer');
    const pendingInternal = tickets.filter((t) => t.status === 'pending_internal');
    const totalPending = pendingCustomer.length + pendingInternal.length;

    const oldPendingCustomer = pendingCustomer.filter((t) => hoursSince(t.updated_at || t.created_at) / 24 > 3).length;

    if (totalPending > 15 || oldPendingCustomer > 5) {
      return {
        type: 'info',
        severity: 'medium',
        icon: 'MdTimer',
        title: 'High Pending Ticket Count',
        message: `${totalPending} tickets pending action: ${pendingCustomer.length} awaiting customer response (${oldPendingCustomer} stale), ${pendingInternal.length} awaiting internal action. Follow-up needed.`,
        action: 'View Pending',
        actionType: 'filter',
        actionPayload: { filter: 'status', value: 'pending_customer' },
        metrics: {
          pendingCustomer: pendingCustomer.length,
          pendingInternal: pendingInternal.length,
          total: totalPending,
          staleCustomer: oldPendingCustomer,
          avgPendingDays: Number((this._calculateAvgAge(pendingCustomer.concat(pendingInternal)) / 24).toFixed(1)),
        },
        recommendations: [
          'Set up automated reminder system for pending customer tickets',
          'Review and escalate internal dependencies for pending internal tickets',
          'Consider closing stale pending customer tickets with final notice',
        ],
        priority: 5,
      };
    }

    return null;
  }

  /**
   * NEW: Analyze ticket trends over time
   */
  analyzeTicketTrends(tickets) {
    const last7Days = tickets.filter((t) => hoursSince(t.created_at) / 24 <= 7);
    const prev7Days = tickets.filter((t) => {
      const age = hoursSince(t.created_at) / 24;
      return age > 7 && age <= 14;
    });

    if (prev7Days.length < 5 || last7Days.length === 0) return null; // --- UPDATE: need a real baseline

    const changePercent = ((last7Days.length - prev7Days.length) / prev7Days.length * 100).toFixed(1);
    const change = parseFloat(changePercent);

    if (Math.abs(change) > 30) {
      const isIncrease = change > 0;
      return {
        key: isIncrease ? 'hd.volume_up' : 'hd.volume_down',
        type: isIncrease ? 'warning' : 'success',
        severity: isIncrease ? (Math.abs(change) > 50 ? 'high' : 'medium') : 'low',
        icon: isIncrease ? 'MdTrendingUp' : 'MdCheckCircle',
        title: `${isIncrease ? 'Increasing' : 'Decreasing'} Ticket Volume`,
        message: `Ticket volume ${isIncrease ? 'increased' : 'decreased'} by ${Math.abs(change)}% in last 7 days (${last7Days.length} vs ${prev7Days.length}). ${isIncrease ? 'Prepare for higher demand.' : 'Good trend!'}`,
        action: null,
        metrics: {
          last7Days: last7Days.length,
          prev7Days: prev7Days.length,
          changePercent,
          direction: isIncrease ? 'up' : 'down',
        },
        recommendations: isIncrease ? [
          'Review staffing levels and consider additional support',
          'Identify root causes for volume increase (weather, one road, an outage)',
          'Prepare proactive communications if issue is widespread',
        ] : [
          'Document what contributed to the decrease',
          'Use this opportunity to clear older tickets',
        ],
        priority: isIncrease ? 3 : 8,
      };
    }

    return null;
  }

  /**
   * NEW: Analyze customer satisfaction indicators
   * --- UPDATE: reopen_count (DB column) and reopened_count; customer_satisfaction_score
   */
  analyzeCustomerSatisfaction(tickets) {
    if (tickets.length < 10) return null;
    const reopened = tickets.filter((t) => Number(t.reopen_count ?? t.reopened_count) > 0).length;
    const highMessageCount = tickets.filter((t) => (t.message_count || 0) > 10).length;
    const rated = tickets.filter((t) => t.customer_satisfaction_score !== null && t.customer_satisfaction_score !== undefined);
    const csat = rated.length ? rated.reduce((a, t) => a + Number(t.customer_satisfaction_score), 0) / rated.length : null;

    if (reopened > tickets.length * 0.1 || highMessageCount > tickets.length * 0.15 || (rated.length >= 5 && csat < 3.5)) {
      return {
        type: 'warning',
        severity: 'medium',
        icon: 'MdInfo',
        title: 'Customer Satisfaction Alert',
        message: `${reopened} tickets reopened, ${highMessageCount} with excessive back-and-forth${csat !== null ? `, average rating ${csat.toFixed(1)}/5` : ''}. May indicate resolution quality issues or unclear communication.`,
        action: 'Review Quality',
        actionType: 'quality-review',
        actionPayload: { reviewType: 'satisfaction' },
        metrics: {
          reopened,
          highMessageCount,
          reopenRate: ((reopened / tickets.length) * 100).toFixed(1),
          csat: csat !== null ? Number(csat.toFixed(1)) : null,
        },
        recommendations: [
          'Review reopened tickets for common issues',
          'Confirm the fix with the caller before closing',
          'Implement quality assurance reviews',
        ],
        priority: 3,
      };
    }

    return null;
  }

  /**
   * --- UPDATE: escalated tickets still open
   */
  analyzeEscalations(tickets) {
    const esc = tickets.filter((t) => isActive(t) && (t.is_escalated === 1 || t.is_escalated === true));
    if (esc.length <= 3) return null;
    return {
      type: 'warning',
      severity: esc.length > 10 ? 'high' : 'medium',
      icon: 'MdTrendingUp',
      title: 'Escalated tickets open',
      message: `${esc.length} escalated tickets are still open.`,
      action: 'View Escalated',
      actionType: 'filter',
      actionPayload: { filter: 'escalated', value: true },
      metrics: { escalated: esc.length },
      recommendations: ['Supervisor review of escalated items daily.'],
      priority: 2,
    };
  }

  /**
   * Export insights to JSON
   */
  exportInsights(insights) {
    this.log('info', 'Exporting insights', { count: insights.length });

    const exportData = {
      generatedAt: new Date().toISOString(),
      version: this.version,
      insightCount: insights.length,
      insights: insights.map((insight, index) => ({
        ...insight,
        exportedAt: new Date().toISOString(),
        index: index + 1,
      })),
      metadata: {
        cacheSize: this.cache.size,
        performanceMetricsCount: this.performanceMetrics.length,
        historyLength: this.insightHistory.length,
        server: this.lastMeta, // --- UPDATE: engine timing, stats, briefing
      },
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Get comprehensive statistics
   */
  getStats() {
    return {
      version: this.version,
      cacheSize: this.cache.size,
      cacheTTL: this.cacheTTL,
      performanceMetrics: this.performanceMetrics.slice(-10), // Last 10
      insightHistory: this.insightHistory.slice(-20), // Last 20
      avgGenerationTime: this._calculateAvgMetric('generateInsights'),
      avgServerTime: this._calculateAvgMetric('getServerInsights'), // --- UPDATE
    };
  }

  /**
   * Helper: Calculate average for a specific metric
   */
  _calculateAvgMetric(operation) {
    const relevant = this.performanceMetrics.filter((m) => m.operation === operation);
    if (relevant.length === 0) return 0;
    const sum = relevant.reduce((s, m) => s + m.duration, 0);
    return (sum / relevant.length).toFixed(2);
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    const stats = {
      size: this.cache.size,
      ttl: this.cacheTTL,
      keys: Array.from(this.cache.keys()),
      oldestEntry: null,
      newestEntry: null,
    };

    if (this.cache.size > 0) {
      const entries = Array.from(this.cache.values());
      const timestamps = entries.map((e) => e.timestamp);
      stats.oldestEntry = new Date(Math.min(...timestamps)).toISOString();
      stats.newestEntry = new Date(Math.max(...timestamps)).toISOString();
    }

    this.log('debug', 'Cache statistics', stats);
    return stats;
  }
}

// Create and export singleton instance
const helpdeskAIInsightsService = new HelpdeskAIInsightsService();

// Enable debug mode in development
if (process.env.NODE_ENV === 'development') {
  helpdeskAIInsightsService.setDebugMode(true);
}

export default helpdeskAIInsightsService;
